import React from "react";

import { CheckCircle, ExpandMore } from "@mui/icons-material";
import { Box, Collapse, Paper, Typography, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import { BulletinSectionId } from "@/components/alerts/add-v2/sections/sectionIds";

import { sectionAnchorId } from "./sectionAnchors";

interface FormSectionProps {
  id: BulletinSectionId;
  title: string;
  description?: string;
  /** Drives the header tick and the rail's completion state. */
  complete: boolean;
  expanded: boolean;
  onToggle: (id: BulletinSectionId, expanded: boolean) => void;
  /** Focus landing anywhere in this section makes it the active one in the rail. */
  onActivate: (id: BulletinSectionId) => void;
  children: React.ReactNode;
}

/**
 * One card of the one-page form.
 *
 * Collapse is presentational — the page owns which sections are open, because completing one opens
 * the next and completed sections deliberately stay open (2026-07-28 design review).
 */
const FormSectionInner = React.forwardRef<HTMLDivElement, FormSectionProps>(
  (
    {
      id,
      title,
      description,
      complete,
      expanded,
      onToggle,
      onActivate,
      children,
    },
    ref,
  ) => {
    const theme = useTheme();
    const { t } = useTranslation("bulletin/create-v2");

    return (
      <Paper
        ref={ref}
        id={sectionAnchorId(id)}
        // React's onFocus bubbles, so this one handler covers every field and button in the
        // section — including ones added later — while the id stays in a closure instead of being
        // read back out of the DOM.
        onFocus={() => onActivate(id)}
        elevation={0}
        variant="outlined"
        sx={{
          borderRadius: 2,
          // Jumping from the rail lands here. On xs the sticky `SectionStepper` sits at the top of
          // the same scroll container, so the header needs to clear it or it arrives underneath.
          scrollMarginTop: { xs: theme.spacing(10), sm: theme.spacing(2) },
        }}
      >
        <Box
          role="button"
          tabIndex={0}
          aria-expanded={expanded}
          aria-controls={`${sectionAnchorId(id)}-content`}
          onClick={() => onToggle(id, !expanded)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              onToggle(id, !expanded);
            }
          }}
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: 3,
            py: 2,
            cursor: "pointer",
            "&:hover": { backgroundColor: "action.hover" },
          }}
        >
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="subtitle1" fontWeight={600}>
                {title}
              </Typography>
              {complete && (
                <CheckCircle
                  fontSize="small"
                  color="primary"
                  aria-label={t("sections.complete")}
                />
              )}
            </Box>
            {description && (
              <Typography variant="body2" color="text.secondary">
                {description}
              </Typography>
            )}
          </Box>

          {/*
            Presentational, not a control. The whole header is the button, and this chevron
            carried no `onClick` of its own — as an `IconButton` it only added a second tab
            stop and a nested control for the same toggle. The state a label would have
            announced is already on the container's `aria-expanded`.
          */}
          <Box
            aria-hidden
            sx={{
              display: "flex",
              p: 0.5,
              color: "action.active",
              transform: expanded ? "rotate(180deg)" : "none",
              transition: theme.transitions.create("transform"),
            }}
          >
            <ExpandMore />
          </Box>
        </Box>

        <Collapse in={expanded} unmountOnExit={false}>
          <Box
            id={`${sectionAnchorId(id)}-content`}
            sx={{ px: 3, pt: 1, pb: 3 }}
          >
            {children}
          </Box>
        </Collapse>
      </Paper>
    );
  },
);

FormSectionInner.displayName = "FormSection";

/**
 * Memoised because the page above re-renders on every keystroke: it reads the whole document to
 * derive per-section completion. All six props are stable between those renders — the labels come
 * from `t`, the callbacks from `useCallback`, the ref from `useSectionAnchors` and `children` is a
 * module-level element — so only the section whose `complete` or `expanded` actually flipped
 * re-renders. The sections inside subscribe to their own fields directly, so between the two,
 * decision 8's "one keystroke touches one section" holds.
 */
export const FormSection = React.memo(FormSectionInner);
